/**
 * Decision Engine - Exclusions Management
 *
 * Manages policy exclusion definitions for the decision engine.
 * v2 SafetyCulture format with categories, wording references and policy wording.
 */

import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Form, Input, InputNumber, Switch, Tag } from 'antd';
import { Ban, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type ExclusionFormInput = {
    exclusionId: string;
    exclusionName: string;
    category: string;
    appliesTo: string;
    wordingReference: string | null;
    description: string | null;
    exceptionOverride: string | null;
    policyWording: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<ExclusionFormInput>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/exclusions',
)({
    staticData: {
        metadata: {
            title: 'Exclusions',
        },
    },
    component: ExclusionsManagement,
});

function ExclusionsManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<ExclusionFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    // The form mounts with the drawer, so the values it opens on are held here and passed as `initialValues`.
    const initialValuesRef = useRef<ExclusionFormInput | null>(null);
    const formDirty = useFormDirty();
    const closeRef = useRef<(() => void) | null>(null);
    const formId = 'exclusion-form';

    // Fetch exclusions
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listExclusions.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const exclusions = data?.rows ?? [];

    // Mutations
    const { mutate: createExclusion, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createExclusion.useMutation({
            onSuccess() {
                message.success('Exclusion created.');
                void trpcUtils.claim.decisionEngine.admin.listExclusions.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateExclusion, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updateExclusion.useMutation({
            onSuccess() {
                message.success('Exclusion updated.');
                void trpcUtils.claim.decisionEngine.admin.listExclusions.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteExclusion } = trpc.claim.decisionEngine.admin.deleteExclusion.useMutation({
        onSuccess() {
            message.success('Exclusion deleted.');
            void trpcUtils.claim.decisionEngine.admin.listExclusions.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof exclusions)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('exclusionId', {
                id: 'exclusionId',
                header: 'ID',
                width: 120,
                cell: ({ row }) => <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.exclusionId}</code>,
            }),
            columnHelper.accessor('exclusionName', {
                id: 'exclusionName',
                header: 'Name',
                width: 220,
            }),
            columnHelper.accessor('category', {
                id: 'category',
                header: 'Category',
                width: 160,
                cell: ({ row }) => <Tag>{row.category}</Tag>,
            }),
            columnHelper.accessor('appliesTo', {
                id: 'appliesTo',
                header: 'Applies to',
                width: 200,
                cell: ({ row }) => {
                    const truncated = row.appliesTo.length > 60 ? `${row.appliesTo.slice(0, 60)}...` : row.appliesTo;
                    return <span title={row.appliesTo}>{truncated}</span>;
                },
            }),
            columnHelper.accessor('wordingReference', {
                id: 'wordingReference',
                header: 'Wording ref',
                width: 120,
                cell: ({ row }) => row.wordingReference ?? <span className="text-muted-foreground">—</span>,
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete exclusion"
                            confirmDescription="Are you sure you want to delete this exclusion?"
                            onConfirm={(onSuccess) =>
                                deleteExclusion(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteExclusion],
    );

    const tableProps = usePrismaTable({
        data: exclusions,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
        emptyState: { noRecordsTitle: 'No exclusions yet' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        initialValuesRef.current = {
            exclusionId: '',
            exclusionName: '',
            category: '',
            appliesTo: '',
            wordingReference: null,
            description: null,
            exceptionOverride: null,
            policyWording: null,
            sortOrder: 0,
            isActive: true,
        };
        setDrawerOpen(true);
    };

    const openEditDrawer = (exclusion: (typeof exclusions)[number]) => {
        setEditingId(exclusion.id);
        initialValuesRef.current = {
            exclusionId: exclusion.exclusionId,
            exclusionName: exclusion.exclusionName,
            category: exclusion.category,
            appliesTo: exclusion.appliesTo,
            wordingReference: exclusion.wordingReference,
            description: exclusion.description,
            exceptionOverride: exclusion.exceptionOverride,
            policyWording: exclusion.policyWording,
            sortOrder: exclusion.sortOrder,
            isActive: exclusion.isActive,
        };
        setDrawerOpen(true);
    };

    // Runs after the closing animation, whether the drawer was dismissed or closed after a save.
    const resetDrawer = () => {
        form.resetFields();
        formDirty.markClean();
        initialValuesRef.current = null;
        setDrawerOpen(false);
        setEditingId(null);
    };

    const onFinish = (values: ExclusionFormInput) => {
        const data = {
            exclusionId: values.exclusionId,
            exclusionName: values.exclusionName,
            category: values.category,
            appliesTo: values.appliesTo,
            wordingReference: values.wordingReference,
            description: values.description,
            exceptionOverride: values.exceptionOverride,
            policyWording: values.policyWording,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateExclusion({ id: editingId, data });
        } else {
            createExclusion({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-danger-border bg-danger-subtle p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-danger-subtle">
                    <Ban className="h-5 w-5 text-danger" />
                </div>
                <div>
                    <SectionHeader as="h3" size="small" title="Policy exclusions" className="mb-1" />
                    <p className="text-muted-foreground text-sm">
                        Define policy exclusions that the AI uses to identify claims falling outside coverage. Each
                        exclusion includes category, wording references, and exception override conditions.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add exclusion
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            {drawerOpen && (
                <OverlayPage
                    title={editingId ? 'Edit exclusion' : 'Add exclusion'}
                    size="small"
                    isDirty={formDirty.isDirty}
                    afterClose={resetDrawer}
                    footer={({ requestClose }) => (
                        <DrawerFooter
                            onCancel={requestClose}
                            formId={formId}
                            okText={editingId ? 'Save changes' : 'Create'}
                            okLoading={isCreating || isUpdating}
                        />
                    )}
                >
                    {({ onClose }) => {
                        closeRef.current = onClose;
                        return (
                            <Form<ExclusionFormInput>
                                form={form}
                                {...formDirty.formProps}
                                layout="vertical"
                                onFinish={onFinish}
                                id={formId}
                                clearOnDestroy
                                initialValues={initialValuesRef.current ?? undefined}
                            >
                                <FormItem
                                    label="Exclusion ID"
                                    name="exclusionId"
                                    rules={[{ required: true, message: 'Exclusion ID is required' }]}
                                    tooltip="Unique identifier for this exclusion"
                                >
                                    <Input placeholder="e.g., EX001" />
                                </FormItem>

                                <FormItem
                                    label="Name"
                                    name="exclusionName"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                >
                                    <Input placeholder="e.g., Intentional acts exclusion" />
                                </FormItem>

                                <FormItem
                                    label="Category"
                                    name="category"
                                    rules={[{ required: true, message: 'Category is required' }]}
                                >
                                    <Input placeholder="e.g., General exclusions" />
                                </FormItem>

                                <FormItem
                                    label="Applies to"
                                    name="appliesTo"
                                    rules={[{ required: true, message: 'Applies to is required' }]}
                                >
                                    <Input placeholder="e.g., All insuring clauses" />
                                </FormItem>

                                <FormItem label="Wording reference" name="wordingReference">
                                    <Input placeholder="e.g., Section 5.1(a)" />
                                </FormItem>

                                <FormItem label="Description" name="description">
                                    <Input.TextArea rows={3} placeholder="Detailed description of the exclusion" />
                                </FormItem>

                                <FormItem
                                    label="Exception/override"
                                    name="exceptionOverride"
                                    tooltip="Conditions under which this exclusion may be overridden"
                                >
                                    <Input.TextArea rows={3} placeholder="Any exceptions or override conditions" />
                                </FormItem>

                                <FormItem label="Policy wording" name="policyWording">
                                    <Input.TextArea rows={3} placeholder="Relevant policy wording text" />
                                </FormItem>

                                <div className="mt-4 border-separator border-t pt-4">
                                    <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                                        <InputNumber min={0} className="w-full" />
                                    </FormItem>

                                    <FormItem label="Active" name="isActive" valuePropName="checked">
                                        <Switch />
                                    </FormItem>
                                </div>
                            </Form>
                        );
                    }}
                </OverlayPage>
            )}
        </PageController>
    );
}
